import axios from "axios";
import { ref, Ref } from "vue";
import { tryOnScopeDispose } from "@vueuse/core";
import { useQueryClient, QueryClient } from "@tanstack/vue-query";
import api from "@/services/api";
import type {
  AIAgentContext,
  AIAgentMessage,
  AIAgentSession,
  ChatAttachment,
  ReasoningEffort,
} from "@/services/ai_agent";

/**
 * Extract a human-readable error message from an API error.
 * Prefers the backend's `error` field from the response body,
 * falls back to Axios message, then to a generic fallback.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = "Unknown error",
): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    if (data && typeof data.error === "string") {
      return data.error;
    }
    if (data && typeof data.detail === "string") {
      return data.detail;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

export interface StreamState {
  isStreaming: boolean;
  currentMessage: string;
  toolCalls: {
    name: string;
    status: "running" | "complete";
    duration?: number;
  }[];
  error: string | null;
  tokensUsed: number;
  iteration: number;
}

export interface StreamResult {
  /** True when polling already pushed fresh messages into the query cache. */
  messagesAlreadyCached: boolean;
}

// Includes the consolidated tool names AND the pre-fold legacy names that
// the backend's LEGACY_TOOL_NAME_ALIASES still routes through, so stored
// sessions replaying old tool_calls still invalidate the right caches.
//
// TODO(deprecation): remove the legacy entries in lockstep with
// `LEGACY_TOOL_NAME_ALIASES` in `backend/ai_agents/tools/adapter.py`.
// See that map's TODO comment for the removal trigger (no stored
// AgentMessage references a pre-fold name in the last 90 days). The two
// surfaces are intentionally co-owned: deleting one without the other
// causes silent cache-invalidation drift on replayed sessions.

/** Tool names that modify dashboard data. */
const DASHBOARD_MODIFYING_TOOLS = new Set([
  "update_dashboard",
  "undo_dashboard_update",
  "create_dashboard",
  "create_widget_simple",
  "create_goal_or_kpi_widget",
  "manage_tab",
  "manage_widgets",
  // legacy aliases (pre-fold)
  "create_dashboard_simple",
  "create_dashboard_auto_layout",
  "create_marketing_dashboard",
  "create_dashboard_from_template",
  "add_tab_to_dashboard",
  "remove_tab_from_dashboard",
  "add_widgets_to_tab",
  "replace_tab_widgets",
]);

/** Tool names that modify calculated (custom) metric definitions. */
const CALCULATED_METRIC_MODIFYING_TOOLS = new Set(["manage_calculated_metric"]);

/** Tool names that modify goal or KPI data. */
const GOAL_KPI_MODIFYING_TOOLS = new Set([
  "manage_goal",
  "manage_kpi",
  "create_goal_or_kpi_widget",
  // legacy aliases (pre-fold)
  "create_goal",
  "update_goal",
  "delete_goal",
  "create_kpi",
  "update_kpi",
  "delete_kpi",
]);

/**
 * Invalidate the query caches implied by the given tool names.
 *
 * Shared between the SSE `tool_call_complete` handler and the polling
 * completion path — polling supports proxy chains that block SSE, and a
 * successful calculated-metric or KPI mutation reaching the client via
 * polling must still refresh the same caches (see PR 8060 review).
 */
function invalidateForToolNames(
  queryClient: QueryClient,
  toolNames: Iterable<string>,
): void {
  let dashboard = false;
  let calculatedMetric = false;
  let goalKpi = false;
  for (const name of toolNames) {
    if (DASHBOARD_MODIFYING_TOOLS.has(name)) dashboard = true;
    if (CALCULATED_METRIC_MODIFYING_TOOLS.has(name)) calculatedMetric = true;
    if (GOAL_KPI_MODIFYING_TOOLS.has(name)) goalKpi = true;
  }
  if (dashboard) {
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["dashboards"] });
  }
  if (calculatedMetric) {
    // Column pickers and widget metric selectors read this list, and
    // dashboards render the metric itself. `profile` carries
    // column_preference, which the server strips the deleted metric
    // from — same set the REST delete path invalidates.
    queryClient.invalidateQueries({ queryKey: ["calculatedMetrics"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["profile"] });
  }
  if (goalKpi) {
    queryClient.invalidateQueries({ queryKey: ["goals"] });
    queryClient.invalidateQueries({ queryKey: ["kpis"] });
    queryClient.invalidateQueries({ queryKey: ["kpi"] });
    queryClient.invalidateQueries({ queryKey: ["kpiCalculation"] });
    queryClient.invalidateQueries({ queryKey: ["kpiTimeSeries"] });
  }
}

/** Every tool name that appears in any assistant message. */
function toolNamesFromMessages(messages: AIAgentMessage[]): string[] {
  const names: string[] = [];
  for (const msg of messages) {
    if (msg.role === "assistant" && msg.toolCalls) {
      for (const call of msg.toolCalls) names.push(call.name);
    }
  }
  return names;
}

// Polling runs alongside SSE so responses are picked up even when the
// proxy chain blocks the EventSource stream.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_DURATION_MS = 20 * 60 * 1000; // 20 minutes

/**
 * Return the ID of the last *final* assistant message — one that has
 * actual content and no pending tool calls.  Intermediate assistant
 * messages (with toolCalls) are saved to DB while the agent is still
 * working, so they must be skipped.
 */
function lastFinalAssistantId(messages: AIAgentMessage[]): number | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === "assistant" && (!m.toolCalls || m.toolCalls.length === 0)) {
      return m.id;
    }
  }
  return null;
}

/**
 * Composable for AI Agent SSE streaming.
 *
 * Strategy: SSE + parallel polling that never stops.  SSE gives
 * real-time tool/content updates when the proxy chain cooperates.
 * Polling runs alongside for the entire duration — it watches for a
 * new *final* assistant message (one with content and no tool calls)
 * so it isn't fooled by intermediate assistant messages saved during
 * tool execution.  Whichever delivery path completes first wins.
 *
 * Polling is never cancelled when SSE events arrive because proxy
 * layers on staging/production can buffer events after the initial
 * handshake, leaving the UI stuck if polling was already stopped.
 */

function _log(...args: unknown[]) {
  if (import.meta.env.DEV) {
    console.warn("[AIStream]", new Date().toISOString().slice(11, 23), ...args);
  }
}

export function useAIAgentStream(sessionId: Ref<number | null>) {
  const queryClient = useQueryClient();
  const state = ref<StreamState>({
    isStreaming: false,
    currentMessage: "",
    toolCalls: [],
    error: null,
    tokensUsed: 0,
    iteration: 0,
  });

  let eventSource: EventSource | null = null;
  let pollIntervalId: ReturnType<typeof setInterval> | null = null;
  // Tracks whether the promise has already been settled (by SSE or poll)
  // to avoid double-resolve/reject.
  let settled = false;
  // Stored so cancelSession() can settle the sendMessageStreaming() promise.
  let activeReject: ((reason: Error) => void) | null = null;

  /**
   * Send a message and stream the response.
   *
   * Resolves with a {@link StreamResult} so callers know whether polling
   * already pushed fresh messages into the TanStack query cache.  When
   * `messagesAlreadyCached` is true, an immediate `refetch()` should be
   * avoided — it can overwrite the cached data with a stale API response
   * on production (proxy caching, timing, etc.).
   */
  async function sendMessageStreaming(
    message: string,
    context?: AIAgentContext,
    attachments?: ChatAttachment[],
    effort?: ReasoningEffort,
  ): Promise<StreamResult> {
    if (!sessionId.value) {
      throw new Error("No session ID available");
    }

    // Reset state
    state.value = {
      isStreaming: true,
      currentMessage: "",
      toolCalls: [],
      error: null,
      tokensUsed: 0,
      iteration: 0,
    };
    settled = false;

    // Set by the polling path when it pushes messages into the query
    // cache before resolving.  Exposed via the returned StreamResult.
    let messagesAlreadyCached = false;

    return new Promise<StreamResult>((resolve, reject) => {
      activeReject = reject;
      (async () => {
        try {
          // Step 1: Initialize the stream by POSTing the message
          const initResponse = await api.post(
            `/ai-agents/sessions/${sessionId.value}/chat-stream-init/`,
            { message, context, attachments, effort },
          );

          if (settled) return;

          if (initResponse.data.status !== "initialized") {
            throw new Error("Failed to initialize stream");
          }

          const requestId =
            initResponse.data.requestId || initResponse.data.request_id;
          if (!requestId) {
            throw new Error(
              "No request ID returned from stream initialization",
            );
          }

          // Snapshot the last final assistant message ID so polling can
          // detect when the backend produces a *new* response.  Try the
          // TanStack cache first (should be fresh from the last refetch);
          // only hit the API if the cache is empty.
          const cached =
            queryClient.getQueryData<AIAgentMessage[]>([
              "ai_agent",
              "messages",
              sessionId.value,
            ]) ?? [];
          let lastFinalIdBefore: number | null = null;
          if (cached.length > 0) {
            lastFinalIdBefore = lastFinalAssistantId(cached);
          } else {
            try {
              const { data } = await api.get(
                `/ai-agents/sessions/${sessionId.value}/messages/`,
              );
              lastFinalIdBefore = lastFinalAssistantId(
                data as AIAgentMessage[],
              );
            } catch {
              lastFinalIdBefore = null;
            }
          }
          if (settled) return;
          // ── Settle helpers ────────────────────────────────────
          function finish(): void {
            if (settled) {
              _log("finish() called but already settled");
              return;
            }
            _log("finish()", { messagesAlreadyCached, sseActive });
            settled = true;
            activeReject = null;
            state.value.isStreaming = false;
            cleanup();
            resolve({ messagesAlreadyCached });
          }

          function fail(error: Error): void {
            if (settled) {
              _log("fail() called but already settled:", error.message);
              return;
            }
            _log("fail()", error.message);
            settled = true;
            activeReject = null;
            state.value.error = error.message;
            state.value.isStreaming = false;
            cleanup();
            reject(error);
          }

          // ── SSE connection ────────────────────────────────────
          const baseUrl = api.defaults.baseURL || "";
          const streamUrl = `${baseUrl}${
            baseUrl.endsWith("/") ? "" : "/"
          }ai-agents/sessions/${sessionId.value}/chat-stream/?request_id=${encodeURIComponent(requestId)}`;

          function safeParse(
            raw: string,
            eventType: string,
          ): Record<string, unknown> | null {
            try {
              return JSON.parse(raw) as Record<string, unknown>;
            } catch {
              console.error(`[SSE] Failed to parse ${eventType} data:`, raw);
              fail(new Error(`Invalid ${eventType} data from server`));
              return null;
            }
          }

          eventSource = new EventSource(streamUrl, {
            withCredentials: true,
          });

          // When SSE delivers events, let it handle tool-call state.
          // Polling keeps running alongside as a completion safety net
          // because proxy layers on staging/production can buffer SSE
          // events after the initial message_start gets through.
          let sseActive = false;
          // Number of tool calls already tracked by polling before SSE
          // takes over.  SSE replays all events for the current message
          // from the start, so we skip the first N tool_call_start events
          // that polling already derived.  This is count-based (not
          // name-based) because the same tool can be called multiple
          // times.  The count can be off by one if a tool completes
          // between the last poll and SSE activation, but this is a
          // narrow window and the worst case is a briefly duplicated
          // entry in the UI.
          let toolCallsFromPoll = 0;

          function onSSEEvent(): void {
            if (!sseActive) {
              sseActive = true;
              toolCallsFromPoll = state.value.toolCalls.length;
            }
          }

          eventSource.addEventListener("message_start", () => {
            _log("SSE: message_start");
            onSSEEvent();
          });

          eventSource.addEventListener("thinking", (e) => {
            onSSEEvent();
            const data = safeParse(e.data, "thinking");
            if (!data) return;
            _log("SSE: thinking", data.iteration);
            state.value.iteration = (data.iteration as number) || 0;
          });

          eventSource.addEventListener("content", (e) => {
            onSSEEvent();
            const data = safeParse(e.data, "content");
            if (!data) return;
            const content = (data.content as string) || "";
            _log(
              "SSE: content",
              content.length,
              "chars, preview:",
              content.slice(0, 80),
            );
            state.value.currentMessage = content;
          });

          eventSource.addEventListener("tool_call_start", (e) => {
            onSSEEvent();
            const data = safeParse(e.data, "tool_call_start");
            if (!data) return;
            _log("SSE: tool_call_start", data.tool_name);
            // Skip if polling already tracked this tool call
            if (toolCallsFromPoll > 0) {
              toolCallsFromPoll--;
              return;
            }
            state.value.toolCalls.push({
              name: data.tool_name as string,
              status: "running",
            });
          });

          eventSource.addEventListener("tool_call_complete", (e) => {
            onSSEEvent();
            const data = safeParse(e.data, "tool_call_complete");
            if (!data) return;
            const toolCall = state.value.toolCalls.find(
              (tc) => tc.name === data.tool_name && tc.status === "running",
            );
            if (toolCall) {
              toolCall.status = "complete";
              toolCall.duration = data.duration_ms as number;
            }

            const toolName = data.tool_name as string;
            if (data.success) {
              invalidateForToolNames(queryClient, [toolName]);
            }
          });

          eventSource.addEventListener("complete", (e) => {
            onSSEEvent();
            const data = safeParse(e.data, "complete");
            if (!data) return;
            _log("SSE: complete", { tokens: data.tokens_used });
            state.value.tokensUsed = (data.tokens_used as number) || 0;
            finish();
          });

          eventSource.addEventListener("error", (e) => {
            if (settled) return;
            // Distinguish a real server-sent error event (MessageEvent with
            // data) from a browser connection-drop event (plain Event without
            // data).  Connection drops are handled by onerror which falls
            // back to polling — surfacing them here would show a misleading
            // "An error occurred" to the user.
            const messageEvent = e as MessageEvent;
            if (typeof messageEvent.data !== "string" || !messageEvent.data) {
              return;
            }
            onSSEEvent();
            let errorMessage = "An error occurred";
            try {
              const data = JSON.parse(messageEvent.data);
              errorMessage = data.error || errorMessage;
            } catch {
              errorMessage = messageEvent.data;
            }
            console.error(
              "[SSE] Received error event from server:",
              errorMessage,
            );
            fail(new Error(errorMessage));
          });

          eventSource.onerror = (evt) => {
            _log("SSE: onerror", {
              settled,
              readyState: eventSource?.readyState,
              type: (evt as Event).type,
            });
            if (settled) return;

            // SSE dropped — let polling take over tool-call state
            // updates and restart it if needed.
            sseActive = false;
            if (!pollIntervalId) {
              startPolling();
            }
          };

          // ── Parallel polling safety net ────────────────────────
          const sid = sessionId.value;
          const pollStart = Date.now();
          // Tracks when we first saw session.status === "completed"
          // without a new final message.  After a grace period we
          // resolve anyway — the agent is done.
          let completedSince: number | null = null;
          const COMPLETED_GRACE_MS = 15_000;

          let pollInFlight = false;

          function startPolling(): void {
            if (pollIntervalId) return;
            pollIntervalId = setInterval(async () => {
              if (settled || pollInFlight) return;
              if (Date.now() - pollStart > POLL_MAX_DURATION_MS) {
                fail(new Error("Response timeout"));
                return;
              }

              pollInFlight = true;
              try {
                const { data } = await api.get(
                  `/ai-agents/sessions/${sid}/messages/`,
                );
                if (settled) {
                  _log("POLL: got response but already settled");
                  return;
                }

                const messages = data as AIAgentMessage[];
                const newLastId = lastFinalAssistantId(messages);
                _log("POLL: messages", {
                  count: messages.length,
                  newLastId,
                  lastFinalIdBefore,
                  sseActive,
                });

                // Extract tool call progress and iteration count from
                // the current turn's messages only (after the baseline).
                if (!sseActive) {
                  // Slice to only messages after the last final assistant
                  // message that existed before this turn started.
                  let startIdx = 0;
                  if (lastFinalIdBefore !== null) {
                    const baseIdx = messages.findIndex(
                      (m) => m.id === lastFinalIdBefore,
                    );
                    if (baseIdx !== -1) startIdx = baseIdx + 1;
                  }
                  const currentTurn = messages.slice(startIdx);

                  const derived: StreamState["toolCalls"] = [];
                  let assistantCount = 0;
                  for (let i = 0; i < currentTurn.length; i++) {
                    const msg = currentTurn[i];
                    if (msg.role === "assistant") {
                      assistantCount++;
                      if (msg.toolCalls) {
                        let resultCount = 0;
                        for (let j = i + 1; j < currentTurn.length; j++) {
                          if (currentTurn[j].role === "tool") resultCount++;
                          else break;
                        }
                        for (let k = 0; k < msg.toolCalls.length; k++) {
                          derived.push({
                            name: msg.toolCalls[k].name,
                            status: k < resultCount ? "complete" : "running",
                          });
                        }
                      }
                    }
                  }
                  if (derived.length > 0) {
                    state.value.toolCalls = derived;
                  }
                  state.value.iteration = assistantCount;
                }

                // A new final assistant message appeared
                if (newLastId !== null && newLastId !== lastFinalIdBefore) {
                  _log("POLL: new final message detected, finishing");
                  messagesAlreadyCached = true;
                  queryClient.setQueryData(
                    ["ai_agent", "messages", sid],
                    messages,
                  );
                  // SSE may have delivered no per-tool events (proxy chain
                  // buffered them), so drive invalidation off the messages
                  // we do have — otherwise a successful calculated-metric
                  // create/update/delete leaves stale caches.
                  const baseIdx =
                    lastFinalIdBefore === null
                      ? 0
                      : messages.findIndex((m) => m.id === lastFinalIdBefore) +
                        1;
                  invalidateForToolNames(
                    queryClient,
                    toolNamesFromMessages(messages.slice(baseIdx)),
                  );
                  finish();
                  return;
                }

                // Check session status for terminal states so agent
                // errors don't leave the user stuck for 10 minutes.
                try {
                  const sessionRes = await api.get(
                    `/ai-agents/sessions/${sid}/`,
                  );
                  if (settled) return;
                  const session = sessionRes.data as AIAgentSession;

                  if (session.status === "error") {
                    queryClient.setQueryData(
                      ["ai_agent", "messages", sid],
                      messages,
                    );
                    queryClient.setQueryData(
                      ["ai_agent", "session", sid],
                      session,
                    );
                    fail(new Error("AI agent encountered an error"));
                    return;
                  }

                  if (session.status === "completed") {
                    // Agent finished but no new final message yet —
                    // e.g. hit max iterations, last msg was a tool call.
                    // Give a grace period, then resolve with what we have.
                    completedSince ??= Date.now();
                    if (Date.now() - completedSince > COMPLETED_GRACE_MS) {
                      queryClient.setQueryData(
                        ["ai_agent", "messages", sid],
                        messages,
                      );
                      queryClient.setQueryData(
                        ["ai_agent", "session", sid],
                        session,
                      );
                      finish();
                      return;
                    }
                  } else if (session.status !== "active") {
                    // Other terminal states (completed, archived)
                    queryClient.setQueryData(
                      ["ai_agent", "session", sid],
                      session,
                    );
                    finish();
                    return;
                  }
                } catch {
                  // Non-critical — keep polling
                }
              } catch {
                // Network blip — keep trying
              } finally {
                pollInFlight = false;
              }
            }, POLL_INTERVAL_MS);
          }

          // Start polling immediately alongside SSE
          startPolling();
        } catch (error) {
          state.value.error = getApiErrorMessage(error);
          state.value.isStreaming = false;
          cleanup();
          reject(error);
        }
      })();
    });
  }

  function cleanup() {
    if (pollIntervalId) {
      clearInterval(pollIntervalId);
      pollIntervalId = null;
    }
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
  }

  /**
   * Cancel the current streaming session.
   */
  async function cancelSession(): Promise<void> {
    if (!sessionId.value) {
      throw new Error("No session ID available");
    }

    // Update state IMMEDIATELY to stop the thinking indicator
    state.value.isStreaming = false;
    state.value.currentMessage = "";
    state.value.error = "Cancelling session...";

    // Settle the sendMessageStreaming() promise so callers don't hang.
    if (!settled && activeReject) {
      settled = true;
      activeReject(new Error("Session cancelled"));
      activeReject = null;
    } else {
      settled = true;
    }

    try {
      cleanup();

      const response = await api.post(
        `/ai-agents/sessions/${sessionId.value}/cancel/`,
      );

      if (response.data.status === "already_finished") {
        state.value.error = null;
      } else {
        state.value.error = "Session cancelled";
      }
    } catch (error: unknown) {
      console.error("Failed to cancel session:", error);
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      const errorMessage =
        axiosError?.response?.data?.message || "Failed to cancel session";
      state.value.error = errorMessage;
      throw error;
    }
  }

  tryOnScopeDispose(() => {
    cleanup();
  });

  return {
    state,
    sendMessageStreaming,
    cancelSession,
    cleanup,
  };
}
